import React, { useEffect, useMemo, useState } from "react";
import { Activity, Download, DollarSign, Eye, RefreshCw, Search, ShoppingCart, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BuyAllPurchase,
  BuyAllPurchasesSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./AllPurchases.module.css";

const PAGE_SIZE = 25;

const emptySummary: BuyAllPurchasesSummary = {
  total_purchases: 0,
  total_revenue: 0,
  member_savings: 0,
  fulfillment_rate: 0,
};

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])).toLocaleDateString("en-US");
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US");
};

const tevoStatusLabel = (status: string | null | undefined) =>
  status
    ? status
        .replace(/^order\./i, "")
        .replace(/[_.-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";
const displayOrderStatus = (purchase: BuyAllPurchase) =>
  tevoStatusLabel(purchase.order_status || purchase.status);
const displayTevoStatus = (status: string | null | undefined) =>
  status ? tevoStatusLabel(status) : "N/A";
const orderStatusOptions = [
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "confirmed", label: "Confirmed" },
  { value: "delivered", label: "Delivered" },
  { value: "delivery_reclassified", label: "Delivery Reclassified" },
  { value: "pending", label: "Pending" },
  { value: "pending_seller", label: "Pending Seller" },
  { value: "refunded", label: "Refunded" },
  { value: "seller_confirmed", label: "Seller Confirmed" },
  { value: "te_failed", label: "Te Failed" },
];

const BuyAdminAllPurchases = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BuyAllPurchase[]>([]);
  const [summary, setSummary] = useState<BuyAllPurchasesSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tevoStatusFilter, setTevoStatusFilter] = useState("all");
  const [tevoStatusOptions, setTevoStatusOptions] = useState<string[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadPurchases = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getAllPurchases({
        search: search.trim() || undefined,
        status: statusFilter,
        tevo_status: tevoStatusFilter,
        from: from || undefined,
        to: to || undefined,
        sort_by: sortColumn,
        sort_dir: sortDir,
        prioritize_upcoming: true,
        page,
        limit: PAGE_SIZE,
      });
      setRows(response.data || []);
      setSummary(response.summary || emptySummary);
      setTotal(response.pagination?.total || 0);
      setTevoStatusOptions(response.filters?.tevo_statuses?.filter(Boolean) || []);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load purchases.");
      setRows([]);
      setSummary(emptySummary);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, tevoStatusFilter, from, to, sortColumn, sortDir, page]);

  const sortMarker = (column: string) => (sortColumn === column ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDir((value) => (value === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDir("desc");
    }
    setPage(1);
  };

  const exportCsv = () => {
    const headers = ["Order ID", "Member", "Email", "Event", "Event Date", "Date", "In-Hand Date", "Delivery Type", "Tickets", "Total", "Savings", "Status", "Tevo", "Payment Method"];
    const csvRows = rows.map((row) => [
      row.id,
      row.member,
      row.email,
      row.event,
      row.event_occurs_at,
      row.date,
      row.tickets_in_hand_on,
      row.delivery_type,
      row.tickets,
      row.total,
      row.savings,
      row.order_status,
      row.tevo_status,
      row.payment_method,
    ]);
    const csv = [headers, ...csvRows].map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "all_purchases.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const visibleCount = useMemo(() => Math.min(PAGE_SIZE, rows.length), [rows.length]);

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="all-purchases-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>All Purchases</h1>
          <p>Complete transaction history across all members</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadPurchases()}
            disabled={loading}
            aria-label="Refresh purchases"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--blue`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <ShoppingCart size={18} />
            </div>
            <span className="buy-admin-stat-label">Total Purchases</span>
          </div>
          <strong>{summary.total_purchases.toLocaleString()}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--green`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <DollarSign size={18} />
            </div>
            <span className="buy-admin-stat-label">Total Revenue</span>
          </div>
          <strong className={styles.greenText}>{formatCurrency(summary.total_revenue)}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--blue`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <TrendingUp size={18} />
            </div>
            <span className="buy-admin-stat-label">Member Savings</span>
          </div>
          <strong className={styles.blueText}>{formatCurrency(summary.member_savings)}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--purple`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <Activity size={18} />
            </div>
            <span className="buy-admin-stat-label">Fulfillment Rate</span>
          </div>
          <strong className={styles.purpleText}>{summary.fulfillment_rate.toFixed(1)}%</strong>
        </section>
      </div>

      <section className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder="Search by member, event, or order ID..."
            />
          </div>
          <label className={styles.filterField}>
            <span>Order Status</span>
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
              <option value="all">All Statuses</option>
              {orderStatusOptions.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span>Tevo</span>
            <select value={tevoStatusFilter} onChange={(event) => { setTevoStatusFilter(event.target.value); setPage(1); }}>
              <option value="all">All Statuses</option>
              {tevoStatusOptions.map((status) => (
                <option key={status} value={status}>{tevoStatusLabel(status)}</option>
              ))}
            </select>
          </label>
          <div className={styles.dateRange}>
            <input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
            <input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </div>
          <button className={styles.exportButton} type="button" onClick={exportCsv}>
            <Download size={16} />
            Export
          </button>
        </div>
      </section>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <section className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading purchases...</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>
                    <button type="button" onClick={() => handleSort("member")}>Member{sortMarker("member")}</button>
                  </th>
                  <th>Event</th>
                  <th>Event Date</th>
                  <th>
                    <button type="button" onClick={() => handleSort("date")}>Purchase Date</button>
                  </th>
                  <th>In-Hand Date</th>
                  <th>Delivery Type</th>
                  <th>Tickets</th>
                  <th>
                    <button type="button" onClick={() => handleSort("total")}>Total{sortMarker("total")}</button>
                  </th>
                  <th>Savings</th>
                  <th>Status</th>
                  <th>Tevo</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((purchase) => (
                  <tr key={`${purchase.order_id}-${purchase.id}`}>
                    <td className={styles.orderId}>{purchase.id}</td>
                    <td className={styles.memberColumn}>
                      <button
                        className={styles.memberButton}
                        type="button"
                        onClick={() => purchase.memberId && navigate(`/buy-user-admin-management/${purchase.memberId}`)}
                      >
                        {purchase.member}
                      </button>
                      <span>{purchase.email || "-"}</span>
                    </td>
                    <td className={styles.eventColumn}>
                      <span className={styles.eventName} title={purchase.event}>{purchase.event || "-"}</span>
                    </td>
                    <td className={styles.dateColumn}>{formatDate(purchase.event_occurs_at)}</td>
                    <td className={styles.dateColumn}>{formatDate(purchase.date)}</td>
                    <td className={styles.dateColumn}>{formatDate(purchase.tickets_in_hand_on)}</td>
                    <td>{purchase.delivery_type || "-"}</td>
                    <td className={styles.center}><strong>{purchase.tickets}</strong></td>
                    <td className={styles.right}>{formatCurrency(purchase.total)}</td>
                    <td className={`${styles.right} ${styles.greenText}`}>{formatCurrency(purchase.savings)}</td>
                    <td className={styles.center}>
                      <span className={`${styles.statusBadge} ${styles[`status_${purchase.status}`] || ""}`}>
                        {displayOrderStatus(purchase)}
                      </span>
                    </td>
                    <td className={styles.center}>
                      <span className={purchase.tevo_status ? styles.statusBadge : styles.mutedText}>
                        {displayTevoStatus(purchase.tevo_status)}
                      </span>
                    </td>
                    <td className={styles.center}>
                      <button
                        className={styles.eyeButton}
                        type="button"
                        onClick={() => purchase.memberId && navigate(`/buy-user-admin-management/${purchase.memberId}`)}
                        aria-label="View member"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={13} className={styles.emptyCell}>No purchases found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.footer}>
          <span>Showing {visibleCount} of {total.toLocaleString()} purchases</span>
          <div className={styles.pagination}>
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminAllPurchases;
